import { Injectable } from '@angular/core';

import { GeolocationError } from '../../../Domain/Error/geolocation-error';
import { GeolocationService } from '../../../Domain/geolocation.service';
import { GeolocationOptions } from '../../../Domain/Model/geolocation-options';
import { GeolocationPosition } from '../../../Domain/Model/geolocation-position';

/**
 * Web implementation of the GeolocationService using the browser's navigator.geolocation API
 */
@Injectable()
export class WebGeolocationService extends GeolocationService {
    /**
     * Get the current position using the browser's navigator.geolocation API
     * @param options Optional configuration for the geolocation request
     * @returns A promise that resolves with the current position
     * @throws GeolocationError with human-readable message
     */
    async getCurrentPosition(options?: GeolocationOptions): Promise<GeolocationPosition> {
        if (!navigator.geolocation) {
            throw new GeolocationError('Geolocation is not supported by this browser.');
        }

        if (navigator.permissions) {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            if (permission.state === 'denied') {
                throw new GeolocationError(
                    'Location access is denied. Please check your browser settings to enable location access.'
                );
            }
        }

        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                position => {
                    const mappedPosition: GeolocationPosition = {
                        coords: {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy
                        },
                        timestamp: position.timestamp
                    };
                    resolve(mappedPosition);
                },
                error => {
                    const mappedError = this.mapError(error);
                    reject(mappedError);
                },
                {
                    enableHighAccuracy: options?.enableHighAccuracy ?? true,
                    timeout: options?.timeout ?? 10000,
                    maximumAge: options?.maximumAge ?? 0
                }
            );
        });
    }

    /**
     * Maps web geolocation errors to a standardized format with human-readable messages
     * @param error The error from navigator.geolocation.getCurrentPosition
     * @returns A standardized GeolocationError
     */
    private mapError(error: unknown): GeolocationError {
        if (!(error instanceof GeolocationPositionError) || !error.code) {
            return new GeolocationError('An unexpected error occurred.');
        }

        switch (error.code) {
            case 1: // PERMISSION_DENIED
                return new GeolocationError('Permission denied. Please enable location access.');
            case 2: // POSITION_UNAVAILABLE
                return new GeolocationError('Unable to determine your location. Ensure location services are enabled.');
            case 3: // TIMEOUT
                return new GeolocationError('Taking too long to get your location. Please try again.');
            default:
                return new GeolocationError('An unexpected error occurred.');
        }
    }
}
